import type { DbExecutor } from "../executor";
import { generateId } from "../ids";
import type { Annotation, AnnotationKind, DimensionPrecision, LineWeight } from "../../geometry/annotations";

/**
 * ANNOTATION PERSISTENCE (spec §83-33).
 *
 * Annotations belong to ONE design and ONE floor. Duplicating a design
 * copies them the way it copies everything else; deleting a design takes
 * them with it. They reference geometry by id and NEVER by value, so a
 * dimension reopened tomorrow measures the walls as they are today.
 *
 * Nothing here touches scope, quantities or money: an annotation is a
 * drawing, and the deletion manifest files it under the design that owns
 * it.
 */

interface Row {
  id: string;
  design_id: string;
  floor_id: string;
  kind: string;
  a_kind: string;
  a_target_id: string | null;
  a_reference: string;
  a_x: number;
  a_y: number;
  b_kind: string | null;
  b_target_id: string | null;
  b_reference: string;
  b_x: number;
  b_y: number;
  offset_x: number;
  offset_y: number;
  text: string;
  font_scale: number;
  line_weight: string;
  precision: string;
  sort_order: number;
}

const SELECT = `SELECT id, design_id, floor_id, kind, a_kind, a_target_id, a_reference, a_x, a_y,
                       b_kind, b_target_id, b_reference, b_x, b_y, offset_x, offset_y,
                       text, font_scale, line_weight, precision, sort_order
                  FROM design_annotations`;

const map = (row: Row): Annotation => ({
  id: row.id,
  designId: row.design_id,
  floorId: row.floor_id,
  kind: row.kind as AnnotationKind,
  a: {
    kind: row.a_kind as Annotation["a"]["kind"],
    targetId: row.a_target_id,
    reference: row.a_reference as Annotation["a"]["reference"],
    at: { x: Number(row.a_x), y: Number(row.a_y) },
  },
  b:
    row.b_kind === null
      ? null
      : {
          kind: row.b_kind as Annotation["a"]["kind"],
          targetId: row.b_target_id,
          reference: row.b_reference as Annotation["a"]["reference"],
          at: { x: Number(row.b_x), y: Number(row.b_y) },
        },
  offset: { x: Number(row.offset_x), y: Number(row.offset_y) },
  text: row.text,
  fontScale: Number(row.font_scale),
  lineWeight: row.line_weight as LineWeight,
  precision: row.precision as DimensionPrecision,
  sortOrder: Number(row.sort_order),
});

export type AnnotationInput = Omit<Annotation, "id" | "sortOrder"> & { sortOrder?: number };

export class AnnotationRepository {
  constructor(private readonly db: DbExecutor) {}

  async listForFloor(designId: string, floorId: string): Promise<Annotation[]> {
    const rows = await this.db.select<Row>(
      `${SELECT} WHERE design_id = ? AND floor_id = ? AND archived_at IS NULL ORDER BY sort_order, created_at`,
      [designId, floorId],
    );
    return rows.map(map);
  }

  async listForDesign(designId: string): Promise<Annotation[]> {
    const rows = await this.db.select<Row>(
      `${SELECT} WHERE design_id = ? AND archived_at IS NULL ORDER BY sort_order, created_at`,
      [designId],
    );
    return rows.map(map);
  }

  async get(id: string): Promise<Annotation | null> {
    const rows = await this.db.select<Row>(`${SELECT} WHERE id = ?`, [id]);
    return rows.length > 0 ? map(rows[0]) : null;
  }

  async create(input: AnnotationInput): Promise<Annotation> {
    const now = new Date().toISOString();
    const id = generateId();
    const next = await this.db.select<{ n: number | null }>(
      "SELECT MAX(sort_order) AS n FROM design_annotations WHERE design_id = ? AND floor_id = ?",
      [input.designId, input.floorId],
    );
    const sortOrder = input.sortOrder ?? Number(next[0]?.n ?? 0) + 10;
    await this.db.execute(
      `INSERT INTO design_annotations
         (id, design_id, floor_id, kind, a_kind, a_target_id, a_reference, a_x, a_y,
          b_kind, b_target_id, b_reference, b_x, b_y, offset_x, offset_y,
          text, font_scale, line_weight, precision, sort_order, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        id, input.designId, input.floorId, input.kind,
        input.a.kind, input.a.targetId, input.a.reference, Math.round(input.a.at.x), Math.round(input.a.at.y),
        input.b?.kind ?? null, input.b?.targetId ?? null, input.b?.reference ?? "center",
        Math.round(input.b?.at.x ?? 0), Math.round(input.b?.at.y ?? 0),
        Math.round(input.offset.x), Math.round(input.offset.y),
        input.text, input.fontScale, input.lineWeight, input.precision, sortOrder, now, now,
      ],
    );
    return { ...input, id, sortOrder };
  }

  /** Presentation only: position, wording and style — never the value. */
  async update(
    id: string,
    patch: Partial<Pick<Annotation, "offset" | "text" | "fontScale" | "lineWeight" | "precision">> & {
      a?: Annotation["a"];
      b?: Annotation["b"];
    },
  ): Promise<void> {
    const sets: string[] = [];
    const params: unknown[] = [];
    if (patch.offset) {
      sets.push("offset_x = ?", "offset_y = ?");
      params.push(Math.round(patch.offset.x), Math.round(patch.offset.y));
    }
    if (patch.text !== undefined) { sets.push("text = ?"); params.push(patch.text); }
    if (patch.fontScale !== undefined) { sets.push("font_scale = ?"); params.push(patch.fontScale); }
    if (patch.lineWeight !== undefined) { sets.push("line_weight = ?"); params.push(patch.lineWeight); }
    if (patch.precision !== undefined) { sets.push("precision = ?"); params.push(patch.precision); }
    if (patch.a) {
      sets.push("a_kind = ?", "a_target_id = ?", "a_reference = ?", "a_x = ?", "a_y = ?");
      params.push(patch.a.kind, patch.a.targetId, patch.a.reference, Math.round(patch.a.at.x), Math.round(patch.a.at.y));
    }
    if (patch.b !== undefined) {
      sets.push("b_kind = ?", "b_target_id = ?", "b_reference = ?", "b_x = ?", "b_y = ?");
      params.push(
        patch.b?.kind ?? null, patch.b?.targetId ?? null, patch.b?.reference ?? "center",
        Math.round(patch.b?.at.x ?? 0), Math.round(patch.b?.at.y ?? 0),
      );
    }
    if (sets.length === 0) return;
    sets.push("updated_at = ?");
    params.push(new Date().toISOString(), id);
    await this.db.execute(`UPDATE design_annotations SET ${sets.join(", ")} WHERE id = ?`, params);
  }

  /** Delete = archive, so an undo can put it back exactly as it was. */
  async archive(id: string): Promise<void> {
    const now = new Date().toISOString();
    await this.db.execute("UPDATE design_annotations SET archived_at = ?, updated_at = ? WHERE id = ?", [now, now, id]);
  }

  async restore(id: string): Promise<void> {
    await this.db.execute(
      "UPDATE design_annotations SET archived_at = NULL, updated_at = ? WHERE id = ?",
      [new Date().toISOString(), id],
    );
  }

  /** A duplicated design gets its own copy of the drawing (spec §83-33). */
  async copyDesign(fromDesignId: string, toDesignId: string): Promise<number> {
    const source = await this.listForDesign(fromDesignId);
    for (const annotation of source) {
      await this.create({ ...annotation, designId: toDesignId });
    }
    return source.length;
  }
}
